import React, { useEffect, useState } from "react";
import { Loader2, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function DailyPurchaseForm({ open, onClose, suppliers, defaultId, editItem, onSaved, onManageProducts }) {
  const [sid, setSid] = useState("");
  const [date, setDate] = useState(todayISO());
  const [products, setProducts] = useState([]);
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setSid(editItem.supplier_id); setDate(editItem.date);
      setQty(Object.fromEntries(editItem.items.map((i) => [i.product_id, String(i.qty)])));
    } else { setSid(defaultId || suppliers[0]?.id || ""); setDate(todayISO()); setQty({}); }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (!sid) return setProducts([]);
    api.get("/supplier-products", { params: { supplier_id: sid } }).then((r) => setProducts(r.data));
  }, [sid, open]);

  const total = products.reduce((s, p) => s + (parseFloat(qty[p.id]) || 0) * p.price, 0);

  const save = async () => {
    const items = products.filter((p) => parseFloat(qty[p.id]) > 0).map((p) => ({ product_id: p.id, qty: parseFloat(qty[p.id]) }));
    if (!sid || items.length === 0) { toast.error("En az bir ürün için adet girin"); return; }
    setSaving(true);
    try {
      const body = { supplier_id: sid, date, items };
      if (editItem) await api.put(`/supplier-purchases/daily/${editItem.id}`, body); else await api.post("/supplier-purchases/daily", body);
      toast.success(`Günlük alım kaydedildi (${TRY(total)}) ve maliyete işlendi`);
      onClose(); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="daily-purchase-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ShoppingBasket className="h-5 w-5 text-primary" />{editItem ? "Günlük Alımı Düzenle" : "Günlük Alım Girişi"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <F label="Tedarikçi">
            <Select value={sid} onValueChange={setSid} disabled={!!editItem}>
              <SelectTrigger data-testid="daily-purchase-supplier"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Tarih"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daily-purchase-date" className="font-mono" /></F>
        </div>
        <div className="rounded-xl border border-border divide-y divide-border/60 max-h-72 overflow-y-auto" data-testid="daily-purchase-items">
          {products.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="daily-purchase-no-products">
              Bu tedarikçi için ürün tanımlı değil.
              <button onClick={onManageProducts} className="block mx-auto mt-2 text-primary underline" data-testid="daily-purchase-manage-products">Ürün ekle</button>
            </div>
          )}
          {products.map((p) => {
            const q = parseFloat(qty[p.id]) || 0;
            return (
              <div key={p.id} className="grid grid-cols-[1fr_5rem_6rem] items-center gap-3 px-3 py-2 text-sm" data-testid={`daily-purchase-item-${p.id}`}>
                <div className="min-w-0"><div className="font-medium truncate">{p.name}</div><div className="text-xs text-muted-foreground font-mono">{TRY(p.price)} / {p.unit}</div></div>
                <Input type="number" min="0" step="any" placeholder="0" value={qty[p.id] || ""} onChange={(e) => setQty({ ...qty, [p.id]: e.target.value })}
                  data-testid={`daily-purchase-qty-${p.id}`} className="h-9 font-mono text-right" />
                <div className={`text-right font-mono font-semibold ${q > 0 ? "" : "text-muted-foreground/50"}`}>{TRY(q * p.price)}</div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between rounded-xl bg-muted/50 px-4 py-3">
          <span className="text-sm text-muted-foreground">Toplam Tutar</span>
          <span className="font-mono text-xl font-bold text-primary" data-testid="daily-purchase-total">{TRY(total)}</span>
        </div>
        <p className="text-xs text-muted-foreground">Tutar tedarikçi hesabına alış olarak işlenir ve gıda maliyetine otomatik eklenir.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving || total <= 0} data-testid="daily-purchase-save" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);
